import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { UfosService } from './ufos.service';
import { appendPage, CardField, Envelope, fields, Fields, money, NoteCard, Page, PanelState, parseLoadKey, PartyRole, Resource, ResourceData, Section, SourceRecord, text } from './ufos-live.types';

@Component({ selector: 'app-ufos', templateUrl: './ufos.component.html', styleUrls: ['./ufos.component.scss'] })
export class UfosComponent implements OnChanges, OnDestroy {
  @Input() ladingUuid: string | null = null;
  section: Section = 'overview';
  wo = '';
  loadNumber = '';
  selectedKey = '';
  linkedKey = '';
  states: Partial<Record<Resource, PanelState>> = {};
  readonly sections: { id: Section; label: string; icon: string }[] = [
    { id: 'overview', label: 'Overview', icon: 'fa-route' }, { id: 'notes', label: 'Notes', icon: 'fa-note-sticky' },
    { id: 'parties', label: 'Parties', icon: 'fa-address-book' }, { id: 'financials', label: 'Financials', icon: 'fa-coins' },
    { id: 'references', label: 'References & freight', icon: 'fa-box' }, { id: 'history', label: 'History', icon: 'fa-clock-rotate-left' },
    { id: 'events', label: 'EDI events', icon: 'fa-tower-broadcast' }
  ];
  readonly sectionResources: Record<Section, Resource[]> = {
    overview: ['load', 'details', 'loads'], notes: ['notes'], parties: ['parties'], financials: ['financials'],
    references: ['details', 'purchase_orders', 'releases', 'dimensions'], history: ['history'], events: ['edi_214', 'edi_status']
  };
  readonly notePages: Resource[] = ['additional', 'mapping'];
  readonly roles: PartyRole[] = ['customer', 'carrier', 'shipper', 'consignee'];
  readonly text = text;
  readonly money = money;
  private generation = 0;
  private cancel = new Subject<void>();
  private queue: { resource: Resource; cursor?: string }[] = [];
  private running = false;

  constructor(private ufos: UfosService) {}
  ngOnChanges(): void { this.refresh(); }
  ngOnDestroy(): void { this.generation++; this.cancel.next(); this.cancel.complete(); }

  refresh(): void {
    this.reset();
    this.wo = this.loadNumber = this.selectedKey = this.linkedKey = '';
    if (this.ladingUuid) this.request('resolve');
  }
  private reset(): void {
    this.generation++;
    this.cancel.next();
    this.queue = [];
    this.running = false;
    this.states = {};
  }
  open(section: Section): void {
    this.section = section;
    if (this.selectedKey) this.sectionResources[section].forEach(resource => this.request(resource));
  }
  selectLoad(key: string): void {
    if (key === this.selectedKey) return;
    const loads = this.states.loads?.response ? { ...this.states.loads, loading: false, error: '' } : undefined;
    const resolve = this.states.resolve;
    this.reset();
    this.states = { loads, resolve };
    this.setKey(key);
    this.request('load');
    this.open(this.section);
  }
  private setKey(key: string): void {
    const parts = parseLoadKey(key);
    this.selectedKey = key;
    this.wo = parts.wo;
    this.loadNumber = parts.load;
  }
  loadKey(row: SourceRecord): string { return `${this.wo}:${text(row.fields['load_no'])}:${text(row.fields['id'])}`; }
  request(resource: Resource, cursor?: string, retry = false): void {
    const state = this.state(resource);
    if (state.loading || (!cursor && state.response && !retry)) return;
    this.states[resource] = { ...state, loading: true, error: '' };
    this.queue.push({ resource, cursor });
    void this.drain();
  }
  private async drain(): Promise<void> {
    if (this.running) return;
    this.running = true;
    const generation = this.generation;
    while (this.queue.length && generation === this.generation) {
      const { resource, cursor } = this.queue.shift()!;
      try {
        const scope = resource === 'resolve' ? { uuid: this.ladingUuid || '' } : { wo_number: this.wo, load_number: this.loadNumber };
        const response = await firstValueFrom(this.ufos.read(resource, scope, cursor).pipe(takeUntil(this.cancel)));
        if (generation !== this.generation) return;
        if (!response.ok || !response.data || !response.meta?.captured_at_utc) throw new Error('The live API returned an incomplete response.');
        const data = response.data as unknown as { load_key?: string; key?: string };
        const key = data.load_key || data.key;
        if (resource !== 'resolve' && key && key !== this.selectedKey) throw new Error('The source load identity changed. Refresh to resolve the link again.');
        if (cursor && 'items' in response.data) {
          response.data = appendPage(this.data(resource) as Page, response.data as Page, true);
        }
        this.states[resource] = { loading: false, error: '', response };
        if (resource === 'resolve') {
          this.linkedKey = (response.data as ResourceData['resolve']).load_key;
          this.setKey(this.linkedKey);
          this.request('load');
          this.open(this.section);
        }
        if (resource === 'notes') {
          const notes = response.data as ResourceData['notes'];
          for (const child of ['additional', 'mapping', 'history'] as const) {
            if (notes[child] && !this.states[child]?.response) this.states[child] = { loading: false, error: '', response: { ...response, data: notes[child] } };
          }
        }
      } catch (error) {
        if (generation !== this.generation) return;
        this.states[resource] = { ...this.state(resource), loading: false, error: this.errorMessage(error) };
      }
    }
    if (generation === this.generation) this.running = false;
  }
  private errorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 404) return 'No matching record was found in production UFOs.';
      if (error.status === 409) return 'UFOs could not identify one unambiguous record. Refresh or have the source link checked.';
      if (error.status === 401 || error.status === 403) return 'Your session or account does not have access to this viewer. Sign in again or have access checked.';
      if (error.status === 413) return 'This source response exceeds the note size limit. It has not been truncated.';
      return 'Live UFOs data is unavailable. Retry this section in a moment.';
    }
    return error instanceof Error ? error.message : 'Live UFOs data is unavailable.';
  }
  state(resource: Resource): PanelState { return this.states[resource] || { loading: false, error: '' }; }
  data<K extends Resource>(resource: K): ResourceData[K] | undefined { return this.states[resource]?.response?.data as ResourceData[K] | undefined; }
  page(resource: Resource): Page | undefined { const data = this.data(resource); return data && 'items' in data ? data as Page : undefined; }
  more(resource: Resource): void { const cursor = this.page(resource)?.next_cursor; if (cursor) this.request(resource, cursor); }
  retry(resource: Resource): void { const cursor = this.page(resource)?.has_more ? this.page(resource)?.next_cursor : undefined; this.request(resource, cursor || undefined, true); }
  trackLoadOption(_index: number, choice: { key: string }): string { return choice.key; }
  get loadOptions(): { key: string; label: string }[] {
    const choices = (this.page('loads')?.items || []).map(row => ({ key: this.loadKey(row), label: `${text(row.fields['load_no'])} · ${text(row.fields['shipcity']) || 'Pickup'} → ${text(row.fields['conscity']) || 'Delivery'}` }));
    if (this.selectedKey && !choices.some(item => item.key === this.selectedKey)) choices.unshift({ key: this.selectedKey, label: `Load ${this.loadNumber} · currently viewing` });
    return choices;
  }
  get financialWarnings(): string[] {
    const financials = this.data('financials');
    return financials ? [...financials.customer.warnings, ...financials.carrier.warnings].filter(issue => issue.severity !== 'info').map(issue => issue.message) : [];
  }
  get activeResources(): Resource[] { return this.sectionResources[this.section]; }
  get activeLoading(): boolean { return this.activeResources.some(resource => this.state(resource).loading); }
  get capturedAt(): string | undefined { return this.activeResources.map(resource => this.states[resource]?.response?.meta.captured_at_utc).filter((value): value is string => !!value).sort()[0]; }
  get load(): Fields { return this.data('load') || {}; }
  get notes(): NoteCard[] {
    const notes = this.data('notes');
    if (!notes) return [];
    const cards: NoteCard[] = [];
    const add = (record: SourceRecord | null | undefined, labels: Record<string, string>, scope: string): void => {
      fields(record, labels).forEach(field => cards.push({ title: field.label, body: field.value, scope }));
    };
    add(notes.load, { notes: 'Load notes', ship_notes: 'Pickup instructions', cons_notes: 'Delivery instructions', desc_note: 'Freight notes' }, `Load ${this.loadNumber}`);
    add(notes.order, { custnote: 'Customer notes', carrnote: 'Carrier notes', shipnote: 'Shipper notes', consnote: 'Consignee notes', miscnote: 'General notes', del_notes: 'Delivery notes', pickup_delayed_notes: 'Pickup delay', delivery_delayed_notes: 'Delivery delay', guard_notes: 'Guard notes', comments: 'Comments' }, 'Whole work order');
    this.page('additional')?.items.forEach(row => add(row, { problem_notes: 'Problem notes', hazmat_instr: 'Hazardous goods instructions' }, 'Whole work order'));
    this.page('mapping')?.items.forEach(row => add(row, { notes: 'Integration notes' }, `Load ${this.loadNumber}`));
    return cards;
  }
  get issues(): { message: string; severity: string }[] {
    const issues = this.activeResources.flatMap(resource => this.states[resource]?.response?.issues || []);
    const financials = this.section === 'financials' ? this.data('financials') : undefined;
    if (financials) issues.push(...financials.customer.warnings, ...financials.carrier.warnings, ...financials.identity.warnings);
    return issues.filter((item, i) => issues.findIndex(other => other.code === item.code && other.message === item.message) === i);
  }
  values(record: SourceRecord | null | undefined, labels: Record<string, string>): CardField[] { return fields(record, labels); }
  stamp(date: ValueOrUndefined, time?: ValueOrUndefined): string { return [text(date), text(time)].filter(Boolean).join(' · ') || 'Not recorded'; }
  address(record: SourceRecord | undefined, prefix = ''): string {
    const f = record?.fields || {};
    return [text(f[prefix + 'addr1']), text(f[prefix + 'addr2']), [text(f[prefix + 'city']), text(f[prefix + 'prov']), text(f[prefix + 'postal'])].filter(Boolean).join(', '), this.country(f[prefix + 'country'])].filter(Boolean).join('\n') || 'Address not recorded';
  }
  private country(value: ValueOrUndefined): string {
    const code = text(value);
    return /^[0-9]{6}$/.test(code) ? `Country code: ${code}` : code;
  }
  historyChanges(row: SourceRecord): CardField[] {
    const changes: CardField[] = [];
    for (const [key, label] of Object.entries({ status: 'Status', status2: 'Status detail', billto: 'Customer ID', carrier: 'Carrier ID', cost: 'Cost (source currency)', subtotal: 'Subtotal (source currency)' })) {
      const before = text(row.fields['orig_' + key]);
      const after = text(row.fields['new_' + key]);
      if (before !== after) changes.push({ label, value: `${before || 'Not recorded'} → ${after || 'Not recorded'}` });
    }
    return changes;
  }
  partySnapshot(role: PartyRole): SourceRecord | undefined { return role === 'customer' || role === 'carrier' ? this.data('parties')?.order_snapshot : this.data('parties')?.load_snapshot; }
  partyPrefix(role: PartyRole): string { return { customer: 'cust', carrier: 'carr', shipper: 'ship', consignee: 'cons' }[role]; }
  snapshotName(role: PartyRole): string { return text(this.partySnapshot(role)?.fields[{ customer: 'cust_name', carrier: 'car_name', shipper: 'shipname', consignee: 'cons_name' }[role]]) || 'Name not recorded'; }
  readonly contactLabels = { phone: 'Phone', email: 'Email', contact1: 'Primary contact', con1_phone: 'Contact phone', con1_ext: 'Extension', contact2: 'Other contact', con2_phone: 'Other phone', hrs_operate: 'Operating hours', hours_open: 'Operating hours', hrs_open: 'Opens', hrs_close: 'Closes', active: 'Active', onhold: 'On hold', last_date: 'Last updated', internalnotes: 'Internal notes', comment: 'Notes', comments: 'Comments', special_info: 'Special instructions', rate_notes: 'Rate notes', mc_num: 'MC number', usdot_num: 'USDOT number' };
  readonly referenceLabels = { ship_bol: 'Pickup BOL', cons_bol: 'Delivery BOL', part_no: 'Part number', book_no: 'Booking', pick_no: 'Pickup reference', del_no: 'Delivery reference', rel_no: 'Release', sealno: 'Seal', marks: 'Marks', class_wo: 'Freight class', nmfc: 'NMFC', stackable: 'Stackable', damage: 'Damage recorded', trl_temp_req: 'Required temperature', trl_temp: 'Trailer temperature', trl_temp_c: 'Temperature unit code', dlength: 'Length', dwidth: 'Width', dheight: 'Height', inchcm: 'Dimension unit', measurement: 'Measurement', volume: 'Volume' };
  readonly referencePanels: { resource: Resource; title: string; scope: string; labels: Record<string, string> }[] = [
    { resource: 'purchase_orders', title: 'Purchase orders', scope: 'Whole work order', labels: { po_number: 'PO number', primary_po: 'Primary', edi_stop: 'EDI stop' } },
    { resource: 'releases', title: 'Releases', scope: 'Selected load', labels: { rel_no: 'Release number', pono: 'PO number', location: 'Location', pd_date: 'Pickup / delivery date', rel_dt: 'Release date', qty_204: 'EDI quantity', edi204_ref: 'EDI reference', pickup: 'Pickup', delivery: 'Delivery' } },
    { resource: 'dimensions', title: 'Dimensions', scope: 'Whole work order', labels: { lineno: 'Line', qty: 'Quantity', length: 'Length', width: 'Width', height: 'Height', uom: 'Unit', actual_lbs: 'Actual lb', cf: 'Cubic feet', cw: 'Chargeable weight', lbspercf: 'Density (lb / cu ft)' } }
  ];
  readonly eventPanels: { resource: Resource; title: string; labels: Record<string, string> }[] = [
    { resource: 'edi_214', title: 'EDI 214 exchanges', labels: { edi_descr: 'Status', edi_code: 'Code', edi_type: 'Type', reason_descr: 'Reason', reason_code: 'Reason code', actual_date: 'Actual date', actual_time: 'Actual time', sent_dt: 'Sent', update_dt: 'Updated', send: 'Send flag' } },
    { resource: 'edi_status', title: 'Recorded EDI status', labels: { status_descr: 'Status', status_code: 'Code', date_time: 'Date / time', city: 'City', prov: 'Province / state', carrier_code: 'Carrier', equipmnt: 'Equipment' } }
  ];
  readonly historyLabels = { orig_status: 'Previous status', new_status: 'New status', orig_status2: 'Previous detail', new_status2: 'New detail', orig_billto: 'Previous customer ID', new_billto: 'New customer ID', orig_carrier: 'Previous carrier ID', new_carrier: 'New carrier ID', orig_cost: 'Previous cost (source amount)', new_cost: 'New cost (source amount)', orig_subtotal: 'Previous subtotal (source amount)', new_subtotal: 'New subtotal (source amount)' };
}
type ValueOrUndefined = string | number | boolean | null | undefined;
